/**
 * Intensity-to-colour mapping for radar returns. Modern HD-colour radar
 * (Raymarine Quantum, Garmin Fantom, B&G Halo) uses a multi-stop ramp
 * from black through dark blue, green, yellow, orange, red to white at
 * saturation. Older "classic green" mode uses a single-hue ramp on
 * varying lightness.
 *
 * See docs/radar-visualization.md for the full visual reference.
 */

export type RadarTheme = "hd" | "classic-green";

interface RGBA {
  r: number;
  g: number;
  b: number;
  a: number;
}

/** Modern HD-colour palette stops. Sorted ascending by intensity. */
const HD_STOPS: ReadonlyArray<{ at: number; rgb: [number, number, number] }> = [
  { at: 0.0, rgb: [0, 0, 0] },
  { at: 0.1, rgb: [10, 30, 90] },
  { at: 0.2, rgb: [10, 70, 160] },
  { at: 0.3, rgb: [20, 160, 200] },
  { at: 0.4, rgb: [40, 200, 100] },
  { at: 0.55, rgb: [220, 220, 30] },
  { at: 0.7, rgb: [255, 150, 30] },
  { at: 0.85, rgb: [240, 50, 30] },
  { at: 1.0, rgb: [255, 240, 220] },
];

/** Classic green palette — phosphor pastiche. */
const CLASSIC_STOPS: ReadonlyArray<{ at: number; rgb: [number, number, number] }> = [
  { at: 0.0, rgb: [0, 0, 0] },
  { at: 0.2, rgb: [20, 60, 30] },
  { at: 0.4, rgb: [40, 140, 60] },
  { at: 0.6, rgb: [80, 220, 110] },
  { at: 0.85, rgb: [180, 255, 200] },
  { at: 1.0, rgb: [240, 255, 240] },
];

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function sample(
  stops: ReadonlyArray<{ at: number; rgb: [number, number, number] }>,
  intensity: number,
): [number, number, number] {
  const i = Math.max(0, Math.min(1, intensity));
  for (let s = 0; s < stops.length - 1; s++) {
    const a = stops[s]!;
    const b = stops[s + 1]!;
    if (i >= a.at && i <= b.at) {
      const span = b.at - a.at;
      const t = span === 0 ? 0 : (i - a.at) / span;
      return [
        lerp(a.rgb[0], b.rgb[0], t),
        lerp(a.rgb[1], b.rgb[1], t),
        lerp(a.rgb[2], b.rgb[2], t),
      ];
    }
  }
  return stops[stops.length - 1]!.rgb;
}

/**
 * Map a radar return intensity (0..1) to an RGBA string suitable for
 * canvas fill / stroke / shadow.
 */
export function intensityToColor(
  intensity: number,
  theme: RadarTheme = "hd",
  alpha = 1,
): string {
  const [r, g, b] = sample(theme === "hd" ? HD_STOPS : CLASSIC_STOPS, intensity);
  return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${alpha})`;
}

/**
 * Pre-compute an RGBA tuple — useful when the caller needs to tune the
 * alpha per pixel (e.g. radial gradient stops).
 */
export function intensityToRGBA(
  intensity: number,
  theme: RadarTheme = "hd",
  alpha = 1,
): RGBA {
  const [r, g, b] = sample(theme === "hd" ? HD_STOPS : CLASSIC_STOPS, intensity);
  return { r: Math.round(r), g: Math.round(g), b: Math.round(b), a: alpha };
}

export function rgbaString(c: RGBA): string {
  return `rgba(${c.r}, ${c.g}, ${c.b}, ${c.a})`;
}
